import { useState, useRef, useCallback, useEffect } from "react";
import styles from "./HotelDetails.module.css";
import {
  ArrowLeft, Building2, MapPin, Star, Phone, Mail, Globe,
  Edit2, Image as ImageIcon, Bed, Wifi, Coffee, Car,
  Dumbbell, Utensils, Wind, Waves, ShieldCheck, Users,
  FileText, CheckCircle2, XCircle, AlertCircle, Save,
  X, Plus, Trash2, Upload, Camera, Tag, Pencil, Crown,
  Wine, Croissant, SquareParking, Bus, Snowflake, Flame, Tv,
  Refrigerator, Lock, ArrowUpDown, Shirt, Clock, Bell, BellRing,
  Luggage, Dog, Palmtree, Trees, Baby, Trophy, Flag, Bike,
  Dice5, Briefcase, ShoppingBag, Banknote, Accessibility, CigaretteOff, ImageOff,
  Search, ChevronDown
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../../context/ToastContext";
import {
  getHotelById,
  getHotelOverviewById,
  getHotelCountries,
  getHotelCities,
  getHotelZones,
  mapApiHotelDetailsToUi,
  mapUiHotelDetailsToUpdatePayload,
  setHotelPrimaryImage,
  updateHotelById,
  updateHotelFacilityIcons,
  uploadHotelImage,
  deleteHotelImage,
  galleryKeyToImageType,
} from "../../../../api/products/useHotelsApi";
import { flagSrcForCode } from "../../../../utils/countryFlags";

const MOCK_HOTEL_DETAILS = {
  id: 1,
  hotelName: "Burj Al Arab Jumeirah",
  sunskyMarketingName: "Burj Al Arab – Iconic Sail of Dubai",
  starRating: 5,
  hotelType: "Luxury Resort",
  status: "active",
  contactPerson: "Ahmed Hassan",
  contactEmail: "reservations@burjalarab.com",
  contactPhone: "+971 4 301 7777",
  website: "www.burjalarab.com",
  country: "AE",
  city: "Dubai",
  destination: "DXB",
  zoneCode: "",
  address: "Jumeirah Street, Umm Suqeim 3, Dubai",
  zipCode: "00000",
  latitude: 25.1412,
  longitude: 55.1853,
  mainImage: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1200",
  hotelDescription: "Standing on its own artificial island, the iconic Burj Al Arab Jumeirah is one of the world's most luxurious hotels. This stunning sail-shaped hotel rises 321 meters above the Arabian Gulf and offers unparalleled views, exceptional service, and lavish accommodations.",
  locationDescription: "Located on its own private island 280 meters offshore from Jumeirah Beach, the hotel offers stunning views of the Arabian Gulf. The hotel is 20 minutes from Dubai International Airport.",
  highlights: [
    "24-hour butler service in every suite",
    "Nine world-class restaurants and bars",
    "Private beach access",
    "Talise Spa with 26 treatment rooms",
    "Helipad transfers available",
    "Terrace pools on 18th floor",
    "Complimentary access to Wild Wadi Waterpark",
    "Rolls-Royce chauffeur service"
  ],
  facilities: [
    { icon: "wifi", name: "Free WiFi" },
    { icon: "pool", name: "Outdoor Pool" },
    { icon: "spa", name: "Full-Service Spa" },
    { icon: "gym", name: "Fitness Center" },
    { icon: "restaurant", name: "9 Restaurants" },
    { icon: "parking", name: "Valet Parking" },
    { icon: "concierge", name: "24/7 Concierge" },
    { icon: "room-service", name: "Room Service" },
    { icon: "business", name: "Business Center" },
    { icon: "beach", name: "Private Beach" },
    { icon: "kids", name: "Kids Club" },
    { icon: "tennis", name: "Tennis Court" }
  ],
  imageGallery: {
    lobby: ["https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=600","https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600"],
    exterior: ["https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600","https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600"],
    pool: ["https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=600","https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600"],
    restaurant: ["https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600","https://images.unsplash.com/photo-1552566626-52f8b828add9?w=600"],
    spa: ["https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=600","https://images.unsplash.com/photo-1596178065887-1198b6148b2b?w=600"]
  },
  rooms: [
    {
      id: 1,
      roomType: "Deluxe One Bedroom Suite",
      description: "Spacious suite with panoramic views of the Arabian Gulf, separate living area, and luxury marble bathroom with jacuzzi.",
      size: "170 sqm",
      maxOccupancy: 3,
      bedConfiguration: "1 King Bed",
      mainImage: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600",
      images: ["https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400","https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=400","https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=400"],
      amenities: ["King-size bed with luxury linens","Separate living room","Marble bathroom with jacuzzi","Floor-to-ceiling windows","Butler service","Complimentary minibar","Nespresso machine","Smart TV","Work desk","Premium toiletries"]
    },
    {
      id: 2,
      roomType: "Deluxe Two Bedroom Suite",
      description: "Expansive two-bedroom suite perfect for families, featuring two luxurious bedrooms, spacious living area, and stunning ocean views.",
      size: "240 sqm",
      maxOccupancy: 5,
      bedConfiguration: "1 King + 2 Twin Beds",
      mainImage: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600",
      images: ["https://images.unsplash.com/photo-1590490360182-c33d57733427?w=400","https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=400"],
      amenities: ["Two separate bedrooms","Two marble bathrooms","Large living and dining area","Panoramic sea views","Butler service","Complimentary minibar","In-room entertainment system","Work desk in each room"]
    }
  ],
  notes: "Check-in time is 3:00 PM and check-out time is 12:00 PM. Early check-in and late check-out are subject to availability.",
  disclaimers: "All rates are subject to 10% service charge, 10% municipality fee, and 6% tourism fee."
};

// ─── Tiny reusable components ───────────────────────────────────────────────

const EditableText = ({ value, onChange, className, placeholder, multiline = false, label }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // Keep the draft in sync with the latest value whenever we're not actively
  // editing — otherwise the draft stays stuck on whatever value was present at
  // mount (e.g. the mock data shown before the real hotel loads).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => { onChange(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };



  if (editing) {
    return (
      <div className={styles.editableActive}>
        {multiline
          ? <textarea className={`${styles.editInput} ${styles.editTextarea}`} value={draft} onChange={e => setDraft(e.target.value)} autoFocus placeholder={placeholder} />
          : <input className={styles.editInput} value={draft} onChange={e => setDraft(e.target.value)} autoFocus placeholder={placeholder} />
        }
        <div className={styles.editActions}>
          <button className={styles.saveBtn} onClick={commit}><Save size={13} /> Save</button>
          <button className={styles.cancelBtn} onClick={cancel}><X size={13} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.editableField} ${className || ""}`} onClick={() => setEditing(true)} title="Click to edit">
      <span>{value || <em className={styles.placeholder}>{placeholder}</em>}</span>
      <Pencil size={12} className={styles.editPencil} />
    </div>
  );
};

const ImageUploadZone = ({ src, fallbackSrc, onUpload, className, children }) => {
  const ref = useRef();
  const handleFile = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    onUpload(url);
  };
  return (
    <div className={`${styles.imgUploadWrap} ${className || ""}`} onClick={() => ref.current.click()}>
      {src ? <img src={src} alt="" onError={(e) => { if (fallbackSrc && !e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = fallbackSrc; } }} /> : <div className={styles.imgPlaceholder}>{children || <><Upload size={24}/><span>Upload Image</span></>}</div>}
      <div className={styles.imgUploadOverlay}>
        <Camera size={22} />
        <span>Change Photo</span>
      </div>
      <input ref={ref} type="file" accept="image/*" style={{display:"none"}} onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
};

const StarRatingEdit = ({ value, onChange }) => (
  <div className={styles.starEdit}>
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={20} fill={i <= value ? "#f59e0b" : "none"} stroke={i <= value ? "#f59e0b" : "#d1d5db"} style={{cursor:"pointer"}} onClick={() => onChange(i)} />
    ))}
  </div>
);

// Pickable facility icons. Keys are stored on each facility + persisted per hotel;
// they must stay in sync with guessFacilityIcon() in useHotelsApi.
const FACILITY_ICON_OPTIONS = [
  { key: "wifi",         label: "WiFi",             Comp: Wifi },
  { key: "pool",         label: "Pool",             Comp: Waves },
  { key: "spa",          label: "Spa / Wellness",   Comp: Wind },
  { key: "gym",          label: "Gym / Fitness",    Comp: Dumbbell },
  { key: "restaurant",   label: "Restaurant",       Comp: Utensils },
  { key: "bar",          label: "Bar / Lounge",     Comp: Wine },
  { key: "breakfast",    label: "Breakfast",        Comp: Croissant },
  { key: "coffee",       label: "Coffee / Tea",     Comp: Coffee },
  { key: "parking",      label: "Parking",          Comp: SquareParking },
  { key: "shuttle",      label: "Shuttle",          Comp: Bus },
  { key: "ac",           label: "Air Conditioning", Comp: Snowflake },
  { key: "heating",      label: "Heating",          Comp: Flame },
  { key: "tv",           label: "TV",               Comp: Tv },
  { key: "minibar",      label: "Minibar",          Comp: Refrigerator },
  { key: "safe",         label: "Safe",             Comp: Lock },
  { key: "elevator",     label: "Elevator",         Comp: ArrowUpDown },
  { key: "laundry",      label: "Laundry",          Comp: Shirt },
  { key: "reception24",  label: "24h Reception",    Comp: Clock },
  { key: "concierge",    label: "Concierge",        Comp: BellRing },
  { key: "room-service", label: "Room Service",     Comp: Bell },
  { key: "luggage",      label: "Luggage Storage",  Comp: Luggage },
  { key: "pet",          label: "Pets Allowed",     Comp: Dog },
  { key: "beach",        label: "Beach",            Comp: Palmtree },
  { key: "garden",       label: "Garden / Terrace", Comp: Trees },
  { key: "kids",         label: "Kids / Family",    Comp: Baby },
  { key: "tennis",       label: "Tennis",           Comp: Trophy },
  { key: "golf",         label: "Golf",             Comp: Flag },
  { key: "bike",         label: "Bicycle",          Comp: Bike },
  { key: "casino",       label: "Casino",           Comp: Dice5 },
  { key: "business",     label: "Business / Meeting", Comp: Briefcase },
  { key: "shop",         label: "Shop / Store",     Comp: ShoppingBag },
  { key: "currency",     label: "Currency / ATM",   Comp: Banknote },
  { key: "wheelchair",   label: "Accessible",       Comp: Accessibility },
  { key: "nonsmoking",   label: "Non-smoking",      Comp: CigaretteOff },
  { key: "check",        label: "General",          Comp: CheckCircle2 },
];
const FACILITY_ICON_MAP = Object.fromEntries(FACILITY_ICON_OPTIONS.map((o) => [o.key, o.Comp]));

const getFacilityIcon = (iconName, size = 18) => {
  const Comp = FACILITY_ICON_MAP[iconName] || CheckCircle2;
  return <Comp size={size} />;
};

// Icon button + popover grid to change a facility's icon.
const FacilityIconPicker = ({ value, onPick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className={styles.iconPickerWrap} ref={ref}>
      <button type="button" className={styles.facilityIconBtn} onClick={() => setOpen((o) => !o)} title="Change icon">
        {getFacilityIcon(value)}
        <span className={styles.facilityIconEdit}><Pencil size={9} /></span>
      </button>
      {open && (
        <div className={styles.iconPickerPop}>
          <div className={styles.iconPickerHint}>Choose an icon</div>
          <div className={styles.iconPickerGrid}>
            {FACILITY_ICON_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                title={o.label}
                className={`${styles.iconPickerOpt} ${o.key === value ? styles.iconPickerOptActive : ""}`}
                onClick={() => { onPick(o.key); setOpen(false); }}
              >
                <o.Comp size={16} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Hotelbeds photo URLs default to the small (~320px) rendition, which looks
// stretched on large surfaces. Swap in a bigger variant (xl = 1024px,
// bigger = 800px); callers fall back to the original URL onError since not
// every photo has every rendition. Non-Hotelbeds URLs (OVH uploads, blob
// previews) pass through untouched.
const hbVariant = (url, variant) => {
  if (!url || !url.includes("photos.hotelbeds.com/giata/")) return url;
  if (url.includes(`/giata/${variant}/`)) return url;
  return url.replace("/giata/", `/giata/${variant}/`);
};

// A gallery photo, with the two states a bare <img> cannot express: a shimmer while it
// decodes, and a placeholder when it will never arrive. Suppliers withdraw photos —
// without this the card renders as an empty white box that reads as a broken page.
//
// Loading is a two-step: try the 800px rendition first, fall back to the stored URL,
// and only give up once BOTH have failed.
const GalleryImage = ({ src, alt = "" }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [source, setSource] = useState(() => hbVariant(src, "bigger"));
  const imgRef = useRef(null);

  useEffect(() => {
    setFailed(false);
    setSource(hbVariant(src, "bigger"));
    // A cached image can finish before React attaches onLoad; without this check the
    // shimmer would sit on top of a picture that has already arrived.
    const node = imgRef.current;
    setLoaded(!!node && node.complete && node.naturalWidth > 0);
  }, [src]);

  if (failed) {
    return (
      <div className={styles.galleryImgFallback} title="This photo is no longer available from the supplier">
        <ImageOff size={26} />
        <span>Unavailable</span>
      </div>
    );
  }

  return (
    <>
      {!loaded && <div className={styles.galleryImgSkeleton} aria-hidden="true" />}
      <img
        ref={imgRef}
        className={loaded ? styles.galleryImgLoaded : styles.galleryImgLoading}
        src={source}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (source !== src) setSource(src);   // step 2: the stored URL
          else setFailed(true);                 // both gone — show the placeholder
        }}
      />
    </>
  );
};

// ─── Searchable Geo Dropdown ─────────────────────────────────────────────────

const GeoSearchSelect = ({ options, value, onChange, placeholder, renderOption, loading: extLoading }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const filtered = options.filter((o) =>
    String(o.label || "").toLowerCase().includes(query.toLowerCase()) ||
    String(o.value || "").toLowerCase().includes(query.toLowerCase())
  );

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div className={styles.geoSelectWrap} ref={ref}>
      <button
        type="button"
        className={styles.geoSelectTrigger}
        onClick={() => { setOpen(!open); setQuery(""); }}
      >
        <span className={styles.geoSelectTriggerText}>
          {selected ? (renderOption ? renderOption(selected, true) : selected.label) : <span className={styles.geoSelectPlaceholder}>{placeholder}</span>}
        </span>
        <ChevronDown size={14} className={styles.geoSelectChevron} style={open ? { transform: "rotate(180deg)" } : undefined} />
      </button>
      {open && (
        <div className={styles.geoSelectPanel}>
          <div className={styles.geoSelectSearch}>
            <Search size={14} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className={styles.geoSelectSearchInput}
            />
          </div>
          <div className={styles.geoSelectOptions}>
            {extLoading ? (
              <div className={styles.geoSelectEmpty}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div className={styles.geoSelectEmpty}>No results</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`${styles.geoSelectOption} ${String(o.value) === String(value) ? styles.geoSelectOptionActive : ""}`}
                  onClick={() => { onChange(o); setOpen(false); }}
                >
                  {renderOption ? renderOption(o) : o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const HotelDetails = () => {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [hotel, setHotel] = useState(MOCK_HOTEL_DETAILS);
  const [originalHotel, setOriginalHotel] = useState(MOCK_HOTEL_DETAILS);
  const [selectedRoomImage, setSelectedRoomImage] = useState({});
  const [selectedGalleryCategory, setSelectedGalleryCategory] = useState("lobby");
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [error, setError] = useState("");

  const update = useCallback((path, value) => {
    setHotel(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = Array.isArray(obj[keys[i]]) ? [...obj[keys[i]]] : { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setHasChanges(true);
  }, []);

  const loadHotelDetails = useCallback(async () => {
    if (!hotelId) {
      setLoading(false);
      setError("Invalid hotel id.");
      return;
    }

    setLoading(true);
    setError("");
    setOverviewLoaded(false);
    try {
      const response = await getHotelById(hotelId);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to load hotel details");
      }

      const mapped = mapApiHotelDetailsToUi(response.data);
      if (!mapped) {
        throw new Error("Hotel details response is invalid");
      }

      setHotel(mapped);
      setOriginalHotel(mapped);
      setSelectedGalleryCategory(Object.keys(mapped.imageGallery || {})[0] || "general");
      setSelectedRoomImage({});
      setHasChanges(false);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load hotel details");
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  const mapOverviewHighlights = (highlights = []) =>
    highlights.map((item) => {
      if (!item || typeof item !== "object") return String(item || "");
      const code = item.facilityCode ?? "";
      return item.facilityName || (code ? `Facility ${code}` : "Highlight");
    });

  const mapOverviewImportantInfo = (items = []) =>
    items
      .map((item) => item?.commentText)
      .filter(Boolean)
      .join("\n");

  const mergeOverviewData = useCallback((baseHotel, overviewData) => {
    const next = { ...baseHotel };
    if (overviewData?.name) next.hotelName = overviewData.name;
    if (overviewData?.name && !next.sunskyMarketingName) next.sunskyMarketingName = overviewData.name;
    if (Number.isFinite(overviewData?.stars)) next.starRating = overviewData.stars;
    if (overviewData?.active !== undefined) next.status = overviewData.active ? "active" : "inactive";
    if (overviewData?.city) next.city = overviewData.city;
    if (overviewData?.countryCode) next.country = overviewData.countryCode;
    if (overviewData?.coverImage) next.mainImage = overviewData.coverImage;
    if (overviewData?.description !== undefined && overviewData?.description !== null) {
      next.hotelDescription = overviewData.description;
    }
    if (Array.isArray(overviewData?.highlights) && overviewData.highlights.length) {
      next.highlights = mapOverviewHighlights(overviewData.highlights);
    }
    if (Array.isArray(overviewData?.importantInfo) && overviewData.importantInfo.length) {
      next.notes = mapOverviewImportantInfo(overviewData.importantInfo);
    }
    return next;
  }, []);

  const loadOverviewData = useCallback(async () => {
    if (!hotelId || overviewLoaded) return;
    try {
      const response = await getHotelOverviewById(hotelId);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to load overview");
      }

      const overviewData = response.data || {};
      setHotel((prev) => mergeOverviewData(prev, overviewData));
      if (!hasChanges) {
        setOriginalHotel((prev) => mergeOverviewData(prev, overviewData));
      }
      setOverviewLoaded(true);
    } catch (err) {
      console.error("Failed to load hotel overview", err);
    }
  }, [hasChanges, hotelId, mergeOverviewData, overviewLoaded]);

  useEffect(() => {
    loadHotelDetails();
  }, [loadHotelDetails]);

  useEffect(() => {
    if (activeTab !== "overview" || loading || !hotelId) return;
    loadOverviewData();
  }, [activeTab, hotelId, loading, loadOverviewData]);

  const handleDiscard = () => {
    setHotel(originalHotel);
    setSelectedRoomImage({});
    setSelectedGalleryCategory(Object.keys(originalHotel.imageGallery || {})[0] || "general");
    setHasChanges(false);
  };

  // Gallery photos persist immediately, like the master image: the PUT never carried them, so
  // adding or removing used to change only this screen and was lost on refresh (22 Oct 2026).
  // After a change the gallery is re-read from the server; other unsaved edits are kept.
  const reloadGallery = useCallback(async () => {
    const response = await getHotelById(hotelId);
    const mapped = response?.success ? mapApiHotelDetailsToUi(response.data) : null;
    if (!mapped) return;
    const fresh = { imageGallery: mapped.imageGallery, mainImage: mapped.mainImage };
    setHotel((prev) => ({ ...prev, ...fresh }));
    setOriginalHotel((prev) => ({ ...prev, ...fresh }));
  }, [hotelId]);

  const handleUploadGalleryImages = async (files, categoryKey) => {
    const list = Array.from(files || []);
    if (!hotelId || !list.length) return;
    const type = galleryKeyToImageType(categoryKey);
    const results = await Promise.allSettled(list.map((file) => uploadHotelImage(hotelId, file, type)));
    const failed = results.filter((r) => r.status === "rejected" || !r.value?.success).length;
    await reloadGallery().catch(() => {});
    if (failed) showToast(`${list.length - failed} uploaded, ${failed} failed`, "error");
    else showToast(list.length === 1 ? "Photo uploaded" : `${list.length} photos uploaded`, "success");
  };

  const handleDeleteGalleryImage = async (imageUrl) => {
    if (!hotelId || !imageUrl) return;
    if (!window.confirm("Remove this photo from the hotel? A Hotelbeds photo will not be added back by the automatic photo sync.")) return;
    try {
      const response = await deleteHotelImage(hotelId, imageUrl);
      if (!response?.success) throw new Error(response?.message || "Failed to remove photo");
      await reloadGallery().catch(() => {});
      showToast("Photo removed", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to remove photo", "error");
    }
  };

  // Master image persists immediately (it's stored via image order, not the PUT
  // payload), so it stays out of the unsaved-changes flow — like image upload.
  const handleSetMaster = async (imageUrl) => {
    if (!hotelId || !imageUrl || hotel.mainImage === imageUrl) return;
    try {
      const response = await setHotelPrimaryImage(hotelId, imageUrl);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to update master image");
      }
      setHotel((prev) => ({ ...prev, mainImage: imageUrl }));
      setOriginalHotel((prev) => ({ ...prev, mainImage: imageUrl }));
      showToast("Master image updated", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to update master image", "error");
    }
  };

  const handleSaveChanges = async () => {
    if (!hotelId) return;
    setSaving(true);
    try {
      const payload = mapUiHotelDetailsToUpdatePayload(hotel);
      const response = await updateHotelById(hotelId, payload);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to save hotel details");
      }

      // Persist per-hotel facility icon overrides (keyed "group-code"). Only
      // real (HotelBeds-synced) facilities carry the codes needed to key them.
      const iconMap = {};
      for (const f of hotel.facilities || []) {
        if (f.facilityCode != null && f.facilityGroupCode != null && f.icon) {
          iconMap[`${f.facilityGroupCode}-${f.facilityCode}`] = f.icon;
        }
      }
      await updateHotelFacilityIcons(hotelId, iconMap);

      setOriginalHotel(hotel);
      setHasChanges(false);
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Failed to save hotel details");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "facilities", label: "Facilities" },
    { id: "rooms", label: "Rooms" },
    { id: "gallery", label: "Gallery" },
    { id: "location", label: "Location & Contact" }
  ];

  // ── OVERVIEW TAB ──────────────────────────────────────────────────────────
  const OverviewTab = () => {
    const addHighlight = () => update("highlights", [...hotel.highlights, ""]);
    const updateHighlight = (i, v) => { const arr = [...hotel.highlights]; arr[i] = v; update("highlights", arr); };
    const removeHighlight = (i) => update("highlights", hotel.highlights.filter((_, idx) => idx !== i));

    return (
      <div className={styles.tabContent}>
        {/* Marketing Name Banner */}
        <div className={styles.marketingBanner}>
          <div className={styles.marketingBannerLeft}>
            <Tag size={16} className={styles.marketingIcon} />
            <span className={styles.marketingLabel}>Sunsky Marketing Name</span>
          </div>
          <EditableText
            value={hotel.sunskyMarketingName}
            onChange={v => update("sunskyMarketingName", v)}
            className={styles.marketingNameText}
            placeholder="Enter Sunsky marketing name..."
          />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>About This Hotel</h2>
          </div>
          <EditableText multiline value={hotel.hotelDescription} onChange={v => update("hotelDescription", v)} placeholder="Enter hotel description..." className={styles.descriptionEditable} />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Hotel Highlights</h2>
            <button className={styles.addRowBtn} onClick={addHighlight}><Plus size={14}/> Add Highlight</button>
          </div>
          <div className={styles.highlightGrid}>
            {hotel.highlights.map((h, i) => (
              <div key={i} className={styles.highlightItem}>
                <CheckCircle2 size={16} className={styles.highlightIcon} />
                <EditableText value={h} onChange={v => updateHighlight(i, v)} placeholder="Enter highlight..." className={styles.highlightText} />
                <button className={styles.removeBtn} onClick={() => removeHighlight(i)}><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className={styles.noticeBox}>
          <div className={styles.noticeIcon}><AlertCircle size={20}/></div>
          <div style={{flex:1}}>
            <h3 className={styles.noticeTitle}>Important Information</h3>
            <EditableText multiline value={hotel.notes} onChange={v => update("notes", v)} placeholder="Add important notes..." className={styles.noticeEditable} />
          </div>
        </div>

        <div className={styles.disclaimerBox}>
          <ShieldCheck size={16}/>
          <EditableText value={hotel.disclaimers} onChange={v => update("disclaimers", v)} placeholder="Add disclaimer..." className={styles.disclaimerEditable} />
        </div>
      </div>
    );
  };

  // ── FACILITIES TAB ────────────────────────────────────────────────────────
  const FacilitiesTab = () => {
    const addFacility = () => update("facilities", [...hotel.facilities, { icon: "check", name: "New Facility", facilityCode: null, facilityGroupCode: null }]);
    const updateFacility = (i, name) => { const arr = [...hotel.facilities]; arr[i] = {...arr[i], name}; update("facilities", arr); };
    const updateFacilityIcon = (i, icon) => { const arr = [...hotel.facilities]; arr[i] = {...arr[i], icon}; update("facilities", arr); };
    const removeFacility = (i) => update("facilities", hotel.facilities.filter((_, idx) => idx !== i));

    return (
      <div className={styles.tabContent}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Hotel Facilities & Amenities</h2>
            <button className={styles.addRowBtn} onClick={addFacility}><Plus size={14}/> Add Facility</button>
          </div>
          <p className={styles.sectionHint}>Click a facility's icon to change it. Icon choices are saved per hotel when you save changes.</p>
          <div className={styles.facilitiesGrid}>
            {hotel.facilities.map((f, i) => (
              <div key={i} className={styles.facilityCard}>
                <FacilityIconPicker value={f.icon} onPick={(k) => updateFacilityIcon(i, k)} />
                <EditableText value={f.name} onChange={v => updateFacility(i, v)} placeholder="Facility name" className={styles.facilityNameEditable} />
                <button className={styles.facilityRemove} onClick={() => removeFacility(i)}><X size={12}/></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── ROOMS TAB ─────────────────────────────────────────────────────────────
  const RoomsTab = () => {
    const updateRoom = (roomId, path, value) => {
      update("rooms", hotel.rooms.map(r => r.id === roomId ? { ...r, [path]: value } : r));
    };
    const updateRoomAmenity = (roomId, i, v) => {
      const room = hotel.rooms.find(r => r.id === roomId);
      const arr = [...room.amenities]; arr[i] = v;
      updateRoom(roomId, "amenities", arr);
    };
    const addAmenity = (roomId) => {
      const room = hotel.rooms.find(r => r.id === roomId);
      updateRoom(roomId, "amenities", [...room.amenities, ""]);
    };
    const removeAmenity = (roomId, i) => {
      const room = hotel.rooms.find(r => r.id === roomId);
      updateRoom(roomId, "amenities", room.amenities.filter((_, idx) => idx !== i));
    };
    const addRoomImage = (roomId, url) => {
      const room = hotel.rooms.find(r => r.id === roomId);
      updateRoom(roomId, "images", [...room.images, url]);
    };
    const removeRoomImage = (roomId, idx) => {
      const room = hotel.rooms.find(r => r.id === roomId);
      updateRoom(roomId, "images", room.images.filter((_, i) => i !== idx));
    };
    const addRoom = () => {
      const newRoom = { id: Date.now(), roomType: "New Room Type", description: "", size: "", maxOccupancy: 2, bedConfiguration: "1 King Bed", mainImage: "", images: [], amenities: [] };
      update("rooms", [...hotel.rooms, newRoom]);
    };
    const removeRoom = (roomId) => update("rooms", hotel.rooms.filter(r => r.id !== roomId));

    return (
      <div className={styles.tabContent}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Room Types</h2>
            <button className={styles.addRowBtn} onClick={addRoom}><Plus size={14}/> Add Room</button>
          </div>

          <div className={styles.roomsList}>
            {hotel.rooms.map((room) => (
              <div key={room.id} className={styles.roomCard}>
                <div className={styles.roomCardHeader}>
                  <EditableText value={room.roomType} onChange={v => updateRoom(room.id, "roomType", v)} className={styles.roomTypeEditable} placeholder="Room type name" />
                  <button className={styles.deleteRoomBtn} onClick={() => removeRoom(room.id)}><Trash2 size={14}/> Remove Room</button>
                </div>

                <div className={styles.roomCardBody}>
                  {/* Images */}
                  <div className={styles.roomImageSection}>
                    <ImageUploadZone
                      src={selectedRoomImage[room.id] || room.mainImage}
                      onUpload={url => { updateRoom(room.id, "mainImage", url); setSelectedRoomImage(p => ({...p, [room.id]: url})); }}
                      className={styles.roomMainImage}
                    />
                    <div className={styles.roomThumbnailsRow}>
                      {room.images.map((img, idx) => (
                        <div key={idx} className={`${styles.thumbWrap} ${(selectedRoomImage[room.id] || room.mainImage) === img ? styles.thumbActive : ""}`}>
                          <img src={img} alt="" onClick={() => setSelectedRoomImage(p => ({...p, [room.id]: img}))} />
                          <button className={styles.thumbRemove} onClick={() => removeRoomImage(room.id, idx)}><X size={10}/></button>
                        </div>
                      ))}
                      {/* Add image thumb */}
                      <label className={styles.thumbAdd}>
                        <Plus size={18}/>
                        <input type="file" accept="image/*" style={{display:"none"}} onChange={e => { const f = e.target.files[0]; if(f) addRoomImage(room.id, URL.createObjectURL(f)); }} />
                      </label>
                    </div>
                  </div>

                  {/* Info */}
                  <div className={styles.roomInfo}>
                    <EditableText multiline value={room.description} onChange={v => updateRoom(room.id, "description", v)} placeholder="Room description..." className={styles.roomDescEditable} />

                    <div className={styles.roomSpecsGrid}>
                      <div className={styles.specItem}>
                        <span className={styles.specLabel}>Size</span>
                        <EditableText value={room.size} onChange={v => updateRoom(room.id, "size", v)} placeholder="e.g. 170 sqm" className={styles.specEditable} />
                      </div>
                      <div className={styles.specItem}>
                        <span className={styles.specLabel}>Max Occupancy</span>
                        <EditableText value={String(room.maxOccupancy)} onChange={v => updateRoom(room.id, "maxOccupancy", parseInt(v)||1)} placeholder="e.g. 3" className={styles.specEditable} />
                      </div>
                      <div className={styles.specItem}>
                        <span className={styles.specLabel}>Bed Type</span>
                        <EditableText value={room.bedConfiguration} onChange={v => updateRoom(room.id, "bedConfiguration", v)} placeholder="e.g. 1 King Bed" className={styles.specEditable} />
                      </div>
                    </div>

                    <div className={styles.amenitiesSection}>
                      <div className={styles.amenitiesHeader}>
                        <h4 className={styles.amenitiesTitle}>Room Amenities</h4>
                        <button className={styles.addSmallBtn} onClick={() => addAmenity(room.id)}><Plus size={12}/> Add</button>
                      </div>
                      <div className={styles.amenitiesList}>
                        {room.amenities.map((a, idx) => (
                          <div key={idx} className={styles.amenityItem}>
                            <CheckCircle2 size={13} className={styles.amenityCheck}/>
                            <EditableText value={a} onChange={v => updateRoomAmenity(room.id, idx, v)} placeholder="Amenity..." className={styles.amenityEditable} />
                            <button className={styles.removeBtn} onClick={() => removeAmenity(room.id, idx)}><X size={11}/></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── GALLERY TAB ───────────────────────────────────────────────────────────
  const GalleryTab = () => {
    const galleryRef = useRef();
    const [newCatName, setNewCatName] = useState("");
    const [addingCat, setAddingCat] = useState(false);

    // Uploaded to the server (OVH storage → hotelImages) under the selected category, then re-read.
    const addImages = (files) => handleUploadGalleryImages(files, selectedGalleryCategory);
    // Removed on the server, then re-read.
    const removeGalleryImg = (idx) => handleDeleteGalleryImage((hotel.imageGallery[selectedGalleryCategory] || [])[idx]);
    const addCategory = () => {
      if (!newCatName.trim()) return;
      update("imageGallery", { ...hotel.imageGallery, [newCatName.toLowerCase()]: [] });
      setSelectedGalleryCategory(newCatName.toLowerCase());
      setNewCatName(""); setAddingCat(false);
    };

    return (
      <div className={styles.tabContent}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Image Gallery</h2>
            <label className={styles.addRowBtn}>
              <Upload size={14}/> Upload Photos
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e => addImages(e.target.files)} />
            </label>
          </div>

          <div className={styles.galleryCategories}>
            {Object.keys(hotel.imageGallery).map(cat => (
              <button key={cat} className={`${styles.categoryBtn} ${selectedGalleryCategory===cat ? styles.categoryBtnActive : ""}`} onClick={() => setSelectedGalleryCategory(cat)}>
                {cat.charAt(0).toUpperCase()+cat.slice(1)}
                <span className={styles.catCount}>{hotel.imageGallery[cat].length}</span>
              </button>
            ))}
            {addingCat
              ? <div className={styles.newCatInput}>
                  <input value={newCatName} onChange={e=>setNewCatName(e.target.value)} placeholder="Category name" autoFocus onKeyDown={e=>e.key==="Enter"&&addCategory()} />
                  <button onClick={addCategory}><CheckCircle2 size={14}/></button>
                  <button onClick={()=>setAddingCat(false)}><X size={14}/></button>
                </div>
              : <button className={styles.categoryBtn} onClick={()=>setAddingCat(true)}><Plus size={13}/> New Category</button>
            }
          </div>

          <div className={styles.galleryGrid}>
            {(hotel.imageGallery[selectedGalleryCategory]||[]).map((img, idx) => {
              const isMaster = hotel.mainImage === img;
              return (
              <div key={idx} className={`${styles.galleryItem} ${isMaster ? styles.galleryItemMaster : ""}`}>
                <GalleryImage src={img} />
                {isMaster && <div className={styles.masterBadge}><Crown size={12}/> Master</div>}
                <div className={styles.galleryOverlay}>
                  <button
                    className={`${styles.galleryMasterBtn} ${isMaster ? styles.galleryMasterBtnActive : ""}`}
                    onClick={()=>handleSetMaster(img)}
                    disabled={isMaster}
                    title={isMaster ? "Current master image" : "Set as master image"}
                  >
                    <Crown size={15}/> {isMaster ? "Master" : "Set as Master"}
                  </button>
                  <button className={styles.galleryRemoveBtn} onClick={()=>removeGalleryImg(idx)}><Trash2 size={16}/></button>
                </div>
              </div>
              );
            })}
            <label className={styles.galleryAddCard}>
              <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e => addImages(e.target.files)} />
              <Upload size={28}/>
              <span>Add Photos</span>
            </label>
          </div>
        </div>
      </div>
    );
  };

  // ── LOCATION TAB ──────────────────────────────────────────────────────────
  const LocationTab = () => {
    const [geoCountries, setGeoCountries] = useState([]);
    const [geoCities, setGeoCities] = useState([]);
    const [geoZones, setGeoZones] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [zonesLoading, setZonesLoading] = useState(false);

    useEffect(() => {
      setCountriesLoading(true);
      getHotelCountries()
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setGeoCountries(list.map((c) => ({ value: c.countryCode, label: c.countryName, isoCode: c.isoCode })));
        })
        .catch(() => setGeoCountries([]))
        .finally(() => setCountriesLoading(false));
    }, []);

    useEffect(() => {
      const cc = hotel.country;
      if (!cc) { setGeoCities([]); return; }
      setCitiesLoading(true);
      getHotelCities({ country: cc })
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setGeoCities(list.map((c) => ({ value: c.cityCode, label: c.cityName })));
        })
        .catch(() => setGeoCities([]))
        .finally(() => setCitiesLoading(false));
    }, [hotel.country]);

    useEffect(() => {
      const dest = hotel.destination;
      if (!dest) { setGeoZones([]); return; }
      setZonesLoading(true);
      getHotelZones({ city: dest, country: hotel.country || "" })
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setGeoZones(list.map((z) => ({ value: String(z.zoneCode), label: z.zoneName })));
        })
        .catch(() => setGeoZones([]))
        .finally(() => setZonesLoading(false));
    }, [hotel.destination, hotel.country]);

    const handleCountryChange = (opt) => {
      update("country", opt.value);
      update("destination", "");
      update("zoneCode", null);
      update("zone", "");
      update("city", "");
    };

    const handleCityChange = (opt) => {
      update("destination", opt.value);
      update("city", opt.label);
      update("zoneCode", null);
      update("zone", "");
    };

    const handleZoneChange = (opt) => {
      update("zoneCode", opt.value === "" ? null : Number(opt.value));
      update("zone", opt.label);
    };

    const renderCountryOption = (opt, isTrigger) => (
      <span className={styles.geoCountryOption}>
        {flagSrcForCode(opt.isoCode || opt.value) && (
          <img src={flagSrcForCode(opt.isoCode || opt.value)} alt="" className={styles.geoFlag} />
        )}
        <span>{opt.label}</span>
        {!isTrigger && <span className={styles.geoCode}>{opt.value}</span>}
      </span>
    );

    const zoneOptionsWithCurrent = [...geoZones];
    if (hotel.zoneCode != null && !geoZones.some((z) => String(z.value) === String(hotel.zoneCode))) {
      zoneOptionsWithCurrent.unshift({ value: String(hotel.zoneCode), label: hotel.zone || `Zone ${hotel.zoneCode}` });
    }

    return (
      <div className={styles.tabContent}>
        <div className={styles.locationGrid}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Location Details</h2>
            <div className={styles.infoGroup}>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Country</span>
                <GeoSearchSelect
                  options={geoCountries}
                  value={hotel.country}
                  onChange={handleCountryChange}
                  placeholder="Select country..."
                  renderOption={renderCountryOption}
                  loading={countriesLoading}
                />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>City / Destination</span>
                <GeoSearchSelect
                  options={geoCities}
                  value={hotel.destination}
                  onChange={handleCityChange}
                  placeholder={hotel.country ? "Select city..." : "Select country first"}
                  loading={citiesLoading}
                />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Zone</span>
                <GeoSearchSelect
                  options={zoneOptionsWithCurrent}
                  value={hotel.zoneCode != null ? String(hotel.zoneCode) : ""}
                  onChange={handleZoneChange}
                  placeholder={hotel.destination ? "Select zone..." : "Select city first"}
                  loading={zonesLoading}
                />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Address</span>
                <EditableText value={String(hotel.address || "")} onChange={v=>update("address",v)} placeholder="Enter Address" className={styles.infoEditable} />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Zip Code</span>
                <EditableText value={String(hotel.zipCode || "")} onChange={v=>update("zipCode",v)} placeholder="Enter Zip Code" className={styles.infoEditable} />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Latitude</span>
                <EditableText value={String(hotel.latitude || "")} onChange={v=>update("latitude",v)} placeholder="Enter Latitude" className={styles.infoEditable} />
              </div>
              <div className={styles.infoRow2}>
                <span className={styles.infoLabel}>Longitude</span>
                <EditableText value={String(hotel.longitude || "")} onChange={v=>update("longitude",v)} placeholder="Enter Longitude" className={styles.infoEditable} />
              </div>
            </div>
            <div className={styles.locationDescSection}>
              <span className={styles.infoLabel}>Location Description</span>
              <EditableText multiline value={hotel.locationDescription} onChange={v=>update("locationDescription",v)} placeholder="Describe the location..." className={styles.infoEditableMulti} />
            </div>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Contact Information</h2>
            <div className={styles.infoGroup}>
              {[
                {label:"Contact Person", key:"contactPerson", Icon:Users},
                {label:"Phone", key:"contactPhone", Icon:Phone},
                {label:"Email", key:"contactEmail", Icon:Mail},
                {label:"Website", key:"website", Icon:Globe},
              ].map(({label,key,Icon}) => (
                <div key={key} className={styles.contactRow}>
                  <div className={styles.contactIconWrap}><Icon size={16}/></div>
                  <div className={styles.contactField}>
                    <span className={styles.infoLabel}>{label}</span>
                    <EditableText value={hotel[key]} onChange={v=>update(key,v)} placeholder={`Enter ${label}`} className={styles.infoEditable} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Map Location</h2>
          <div className={styles.mapPlaceholder}>
            <MapPin size={40}/>
            <p>Map integration available here</p>
            <span>{hotel.latitude}, {hotel.longitude}</span>
          </div>
        </div>
      </div>
    );
  };

   return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.saveBanner}>
          <div className={styles.saveBannerLeft}>
            <div className={styles.saveBannerDot} />
            <span>Loading hotel details...</span>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.saveBanner}>
          <div className={styles.saveBannerLeft}>
            <div className={styles.saveBannerDot} />
            <span>{error}</span>
          </div>
          <div className={styles.saveBannerActions}>
            <button className={styles.saveChangesBtn} onClick={loadHotelDetails}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Save Banner */}
      {hasChanges && (
        <div className={styles.saveBanner}>
          <div className={styles.saveBannerLeft}>
            <div className={styles.saveBannerDot} />
            <span>You have unsaved changes</span>
          </div>
          <div className={styles.saveBannerActions}>
            <button className={styles.discardBtn} onClick={handleDiscard} disabled={saving}>
              <X size={14}/> Discard
            </button>
            <button className={styles.saveChangesBtn} onClick={handleSaveChanges} disabled={saving}>
              <Save size={14}/> {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={styles.headerBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}><ArrowLeft size={18}/><span>Back to Hotels</span></button>
        <div className={styles.headerRight}>
          <div className={styles.statusRadioGroup}>
            <label className={`${styles.statusRadioLabel} ${styles.statusLabelActive}`}>
              <input type="radio" name="hotelStatus" value="active" checked={hotel.status === "active"} onChange={() => update("status", "active")} />
              <span className={styles.statusRadioDot} />
              Active
            </label>
            <label className={`${styles.statusRadioLabel} ${styles.statusLabelInactive}`}>
              <input type="radio" name="hotelStatus" value="inactive" checked={hotel.status === "inactive"} onChange={() => update("status", "inactive")} />
              <span className={styles.statusRadioDot} />
              Inactive
            </label>
          </div>
        </div>
      </div>

      {/* Hero — blurred backdrop of the master photo + sharp framed card.
          The blur masks the low-res source, so the image is never shown stretched. */}
      <div className={styles.hero}>
        <div
          className={styles.heroBackdrop}
          style={hotel.mainImage ? { backgroundImage: `url("${hbVariant(hotel.mainImage, "xl")}"), url("${hotel.mainImage}")` } : undefined}
        />
        <div className={styles.heroScrim} />
        <div className={styles.heroContent}>
          <div className={styles.hotelHeader}>
            <div className={styles.hotelIcon}><Building2 size={26}/></div>
            <div className={styles.hotelTitleSection}>
              <EditableText value={hotel.hotelName} onChange={v=>update("hotelName",v)} className={styles.hotelNameEditable} placeholder="Hotel name" />
              <div className={styles.hotelMeta}>
                <StarRatingEdit value={hotel.starRating} onChange={v=>update("starRating",v)} />
                <EditableText value={hotel.hotelType} onChange={v=>update("hotelType",v)} className={styles.hotelTypeEditable} placeholder="Hotel type" />
                <div className={styles.locationBadge}><MapPin size={13}/>
                  <EditableText value={`${hotel.city}, ${hotel.country}`} onChange={v=>{ const [c,co]=v.split(","); update("city",(c||"").trim()); update("country",(co||"").trim()); }} className={styles.locationEditable} placeholder="City, Country" />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.heroMasterCard}>
            <div className={styles.heroMasterFrame}>
              <div className={styles.heroMasterChip}><Crown size={11}/> Master Photo</div>
              <ImageUploadZone
                src={hbVariant(hotel.mainImage, "xl")}
                fallbackSrc={hotel.mainImage}
                onUpload={v=>update("mainImage",v)}
                className={styles.heroMasterImage}
              >
                <Upload size={20}/><span>Add master photo</span>
              </ImageUploadZone>
            </div>
            <div className={styles.heroMasterHint}>Click photo to replace · or use “Set as Master” in the Gallery</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          {tabs.map(t => (
            <button key={t.id} className={`${styles.tab} ${activeTab===t.id?styles.tabActive:""}`} onClick={()=>setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {activeTab==="overview" && <OverviewTab />}
        {activeTab==="facilities" && <FacilitiesTab />}
        {activeTab==="rooms" && <RoomsTab />}
        {activeTab==="gallery" && <GalleryTab />}
        {activeTab==="location" && <LocationTab />}
      </div>
    </div>
  );
};

export default HotelDetails;